import type { LayerData } from '@apps/card/routes/CardCustomizer/components/Layer.types';
import { absolutePhotoUrl } from './mediaService';

/**
 * Tells a monochrome "ink glyph" apart from artwork, by measuring the image itself.
 *
 * Some template marks are ink rather than pictures — the monogram separator, holiday ornaments —
 * and should follow the card's text colour when a customer matches the back to the front. They
 * carry their colour in their pixels, so recolouring means masking: keep the alpha as a stencil and
 * paint a flat colour through it (see `ImageLayer`).
 *
 * That is destructive on anything else — mask a photograph and you get a solid silhouette of it —
 * so which layers qualify has to be decided correctly, and nothing in the template JSON says. All
 * 147 non-editable image layers across the back catalogue are unnamed, and geometry lies: the
 * portrait monogram's slash is a 1500x2100 image that is 99.99% transparent, drawn full-bleed, so
 * any size rule reads it as a full-page illustration.
 *
 * Only the pixels distinguish them, measured against real assets:
 *
 * | asset                          | opaque | colours | verdict |
 * |--------------------------------|--------|---------|---------|
 * | monogram slash (full-bleed)    |  0.00  |    1    | ink     |
 * | monogram slash (18x18)         |  0.07  |    1    | ink     |
 * | photo_monogram photo           |  1.00  |  215    | artwork |
 * | plaid_red background           |  1.00  |    4    | artwork |
 * | large_photo ornament           |  0.06  |    2    | ink     |
 *
 * Both conditions are load-bearing: `plaid_red`'s background has only four colours, so colour count
 * alone nearly misfires, and opacity is what separates it.
 */

/** Above this share of opaque pixels an image is covering its box, so it is a picture, not a mark. */
const MAX_OPAQUE_SHARE = 0.5;

/** Distinct colours allowed among opaque pixels, after quantising away anti-aliasing noise. */
const MAX_DISTINCT_COLORS = 2;

/** Alpha below this reads as background rather than ink, and is what anti-aliased edges fade to. */
const MIN_INK_ALPHA = 32;

/** Long edge the probe samples at. Big enough to catch a thin mark, small enough to stay cheap. */
const SAMPLE_MAX_EDGE = 128;

/**
 * Whether RGBA pixels describe a monochrome mark on a transparent field.
 *
 * Pure, so the thresholds above are testable without a canvas. Colours are quantised to 4 bits per
 * channel: a glyph's anti-aliased edges blend toward the background and would otherwise register as
 * dozens of distinct colours.
 */
export function isInkGlyphPixels(data: Uint8ClampedArray | ReadonlyArray<number>, pixelCount: number): boolean {
  if (pixelCount <= 0) return false;
  let opaque = 0;
  const colors = new Set<number>();
  for (let i = 0; i < pixelCount * 4; i += 4) {
    if (data[i + 3] <= MIN_INK_ALPHA) continue;
    opaque++;
    colors.add(((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4));
    if (colors.size > MAX_DISTINCT_COLORS) return false;
  }
  if (opaque === 0) return false;
  return opaque / pixelCount <= MAX_OPAQUE_SHARE;
}

/**
 * Media-service filenames carry a content hash, so a src always maps to the same bytes and a
 * verdict never goes stale. Promises are cached so concurrent layers sharing an asset probe once.
 */
const verdicts = new Map<string, Promise<boolean>>();

async function probe(src: string): Promise<boolean> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  const url = absolutePhotoUrl(src);
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`could not load ${url}`));
      image.src = url;
    });
    const width = Math.min(image.naturalWidth, SAMPLE_MAX_EDGE);
    const height = Math.min(image.naturalHeight, SAMPLE_MAX_EDGE);
    if (!width || !height) return false;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return false;
    context.drawImage(image, 0, 0, width, height);
    // Throws if the canvas is tainted, i.e. the host refused CORS.
    const { data } = context.getImageData(0, 0, width, height);
    return isInkGlyphPixels(data, width * height);
  } catch {
    // Fail closed. A glyph left in its own colour is a cosmetic miss; masking a photograph by
    // mistake replaces it with a silhouette, so an unmeasurable image is never treated as ink.
    return false;
  }
}

/**
 * The image srcs among `layers` that are ink glyphs, for `applyFrontColorsToBack` to fill.
 *
 * Customer photos (`editable`) are excluded before any network work — they are never ink, and
 * probing them would be pure cost.
 */
export async function findInkGlyphSrcs(layers: ReadonlyArray<LayerData>): Promise<ReadonlySet<string>> {
  const candidates = [...new Set(layers.filter(layer => layer.type === 'image' && !layer.editable).map(layer => (layer as { imageData: { src: string } }).imageData.src))];
  const results = await Promise.all(
    candidates.map(src => {
      const cached = verdicts.get(src) ?? probe(src);
      verdicts.set(src, cached);
      return cached.then(isGlyph => [src, isGlyph] as const);
    }),
  );
  return new Set(results.filter(([, isGlyph]) => isGlyph).map(([src]) => src));
}
